import { useDispatch } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import Router from "./routes/routes";
import { setIsDesktop, setIsMobile } from "store/deviceSlice";
import { useEffect, useState, lazy } from "react";
import { FormProvider } from "contexts/FormContext";
import { Toaster } from "react-hot-toast";
import { CompareProvider } from "contexts/CompareContext";
import { Suspense } from "react";
// import { GlobalFloatingIcons } from "components/GlobalFloatingIcons/GlobalFloatingIcons";
import { WishlistProvider } from "contexts/WishlistContext";
import {
  TooltipManagerProvider,
  TooltipProvider,
} from "components/tooltip/Tooltip";

const CompareStrip = lazy(() => import("components/common/CompareStrip"));
const CookiesPopup = lazy(() => import("components/cookiesPopup/CookiesPopup"));

function App() {
  const dispatch = useDispatch();
  const [showCookiesPopup, setShowCookiesPopup] = useState(false);

  const isMobile = useMediaQuery({ maxWidth: 769 });
  const isDesktop = useMediaQuery({ minWidth: 1024 });

  useEffect(() => {
    dispatch(setIsMobile(isMobile));
    dispatch(setIsDesktop(isDesktop));
  }, [isMobile, isDesktop, dispatch]);

  useEffect(() => {
    const cookiesAccepted = localStorage.getItem("cookiesAccepted");
    const cookiesDeclined = localStorage.getItem("cookiesDeclined");

    if (!cookiesAccepted && !cookiesDeclined) {
      const timer = setTimeout(() => {
        setShowCookiesPopup(true);
        document.body.style.overflow = "hidden";
      }, 5000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, []);

  useEffect(() => {
    if (showCookiesPopup) {
      document.body.style.setProperty("overflow", "hidden", "important");
      document.documentElement.style.setProperty(
        "overflow",
        "hidden",
        "important"
      );

      const scrollContainers = document.querySelectorAll(
        ".snap-y, .snap-mandatory, .overflow-y-scroll"
      );
      scrollContainers.forEach((container) => {
        container.style.setProperty("overflow", "hidden", "important");
        container.style.setProperty("scroll-snap-type", "none", "important");
      });
    } else {
      document.body.style.removeProperty("overflow");
      document.documentElement.style.removeProperty("overflow");

      const scrollContainers = document.querySelectorAll(
        ".snap-y, .snap-mandatory, .overflow-y-scroll"
      );
      scrollContainers.forEach((container) => {
        container.style.removeProperty("overflow");
        container.style.removeProperty("scroll-snap-type");
      });
    }
  }, [showCookiesPopup]);

  const handleAcceptCookies = (e) => {
    e.preventDefault();
    localStorage.setItem("cookiesAccepted", "true");
    setShowCookiesPopup(false);
  };

  const handleDeclineCookies = (e) => {
    e.preventDefault();
    localStorage.setItem("cookiesDeclined", "true");
    setShowCookiesPopup(false);
  };

  useEffect(() => {
    let lastHeight = window.visualViewport?.height || window.innerHeight;
    const setViewportHeight = () => {
      const newHeight = window.visualViewport?.height || window.innerHeight;
      if (newHeight >= lastHeight || newHeight > 400) {
        lastHeight = newHeight;
        const vh = newHeight * 0.01;
        document.documentElement.style.setProperty("--vh", `${vh}px`);
      } else {
        const vh = lastHeight * 0.01;
        document.documentElement.style.setProperty("--vh", `${vh}px`);
      }
    };

    setViewportHeight();
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", setViewportHeight);
    } else {
      window.addEventListener("resize", setViewportHeight);
      window.addEventListener("orientationchange", setViewportHeight);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", setViewportHeight);
      } else {
        window.removeEventListener("resize", setViewportHeight);
        window.removeEventListener("orientationchange", setViewportHeight);
      }
    };
  }, []);

  return (
    <BrowserRouter>
      <TooltipManagerProvider>
        <TooltipProvider>
          <FormProvider>
            <CompareProvider>
              <WishlistProvider>
                <Suspense fallback={null}>
                  <Router />
                </Suspense>
                <Suspense fallback={null}>
                  <CompareStrip />
                  {/* <GlobalFloatingIcons /> */}
                </Suspense>

                {showCookiesPopup && (
                  <Suspense fallback={null}>
                    <div className="fixed bottom-2 left-2 lg:bottom-4 lg:left-4 z-[999999999999] animate-slide-up">
                      <CookiesPopup
                        onAccept={handleAcceptCookies}
                        onDecline={handleDeclineCookies}
                      />
                    </div>
                  </Suspense>
                )}
              </WishlistProvider>
            </CompareProvider>
          </FormProvider>
          <Toaster position="top-right" reverseOrder={false} />
        </TooltipProvider>
      </TooltipManagerProvider>
    </BrowserRouter>
  );
}

export default App;
